import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertMotivoPerdaSchema, type MotivoPerda } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { z } from "zod";

import { PageHeader, ALVO_TOQUE, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { AdminLista, AdminListaItem, AdminTabela } from "@/components/admin/admin-lista";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { combina } from "@/lib/busca";
import { PageSkeleton } from "@/components/data/page-skeleton";
type MotivoPerdaFormValues = z.infer<typeof insertMotivoPerdaSchema>;

const COLUNAS_MOTIVOS = ["16rem", null, "9rem", "6rem"];

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_BUSCA = { q: "" };

export default function MotivosPerdasPage() {
  // Busca NO CLIENTE, sobre a lista já carregada.
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState<MotivoPerda | null>(null);

  const { data: motivos, isLoading } = useQuery<MotivoPerda[]>({
    queryKey: ["/api/motivos-perdas"],
  });

  const visiveis = (motivos ?? []).filter(m => combina(busca, m.nome, m.descricao));

  const form = useForm<MotivoPerdaFormValues>({
    resolver: zodResolver(insertMotivoPerdaSchema),
    defaultValues: {
      nome: "",
      descricao: "",
      ativo: true,
    },
  });

  const handleOpenDialog = (motivo?: MotivoPerda) => {
    if (motivo) {
      setEditingMotivo(motivo);
      form.reset({
        nome: motivo.nome,
        descricao: motivo.descricao || "",
        ativo: motivo.ativo,
      });
    } else {
      setEditingMotivo(null);
      form.reset({
        nome: "",
        descricao: "",
        ativo: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (data: MotivoPerdaFormValues) => {
    try {
      if (editingMotivo) {
        await apiRequest("PATCH", `/api/motivos-perdas/${editingMotivo.id}`, data);
        toast({
          title: "Sucesso",
          description: "Motivo de perda atualizado com sucesso",
        });
      } else {
        await apiRequest("POST", "/api/motivos-perdas", data);
        toast({
          title: "Sucesso",
          description: "Motivo de perda criado com sucesso",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/motivos-perdas"] });
      setDialogOpen(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Ocorreu um erro ao salvar o motivo",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja desativar este motivo de perda?")) {
      return;
    }

    try {
      await apiRequest("DELETE", `/api/motivos-perdas/${id}`);
      toast({
        title: "Sucesso",
        description: "Motivo de perda desativado com sucesso",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/motivos-perdas"] });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Ocorreu um erro ao desativar o motivo",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="space-y-6 md:space-y-8"><PageSkeleton variante="cards" linhas={4} /></div>;
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        titulo="Motivos de perda"
        descricao="Gerenciar motivos de perda de produção"
        acao={
          <Button onClick={() => handleOpenDialog()} className={ALVO_TOQUE_BOTAO} data-testid="button-new-motivo">
            <Plus className="h-4 w-4 mr-2" />
            Novo motivo
          </Button>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMotivo ? "Editar Motivo de Perda" : "Novo Motivo de Perda"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ex: SECA, DOENCA" data-testid="input-nome-motivo" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="descricao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição (opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Descrição do motivo de perda"
                        rows={3}
                        data-testid="input-descricao-motivo"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Rodapé do dialog: botões em largura total no celular. */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end [&>button]:min-h-11 sm:[&>button]:min-h-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="button-cancel-motivo"
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="button-submit-motivo">
                  {editingMotivo ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome" testId="input-busca-motivos" />}
      >
        <span />
      </PageToolbar>

      {!motivos || motivos.length === 0 ? (
        <EmptyState
          icone={AlertCircle}
          titulo="Nenhum motivo cadastrado"
          descricao="O motivo é o que explica um cacho que não virou colheita — sem ele a perda entra no ciclo sem causa."
          acao={
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />Criar primeiro motivo
            </Button>
          }
          testId="empty-motivos"
        />
      ) : visiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          <AdminLista>
            {visiveis.map(motivo => (
              <AdminListaItem
                key={motivo.id}
                testId={`card-motivo-${motivo.id}`}
                atenuado={!motivo.ativo}
                identidade={motivo.nome}
                meta={
                  <>
                    {/* Ativo/inativo É estado — muda por ação e o usuário pode
                        querer reverter. Aqui o tom semântico é o certo. */}
                    {motivo.ativo
                      ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
                      : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                    {motivo.descricao && <span className="truncate">{motivo.descricao}</span>}
                  </>
                }
                acoes={
                  <>
                    <Button
                      size="icon" variant="ghost" className={ALVO_TOQUE}
                      aria-label={`Editar ${motivo.nome}`}
                      onClick={() => handleOpenDialog(motivo)}
                      data-testid={`button-edit-motivo-${motivo.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {motivo.ativo && (
                      <Button
                        size="icon" variant="ghost" className={ALVO_TOQUE}
                        aria-label={`Desativar ${motivo.nome}`}
                        onClick={() => handleDelete(motivo.id)}
                        data-testid={`button-delete-motivo-${motivo.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </>
                }
              />
            ))}
          </AdminLista>

          <AdminTabela>
            <DataTable colunas={COLUNAS_MOTIVOS}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Nome</DataTableHead>
                  <DataTableHead>Descrição</DataTableHead>
                  <DataTableHead>Situação</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {visiveis.map(motivo => (
                  <DataTableRow key={motivo.id} data-testid={`row-motivo-${motivo.id}`}>
                    <DataTableCell destaque className={`truncate ${!motivo.ativo ? "opacity-50" : ""}`}>
                      {motivo.nome}
                    </DataTableCell>
                    <DataTableCell className={`text-muted-foreground truncate ${!motivo.ativo ? "opacity-50" : ""}`}>
                      {motivo.descricao || "—"}
                    </DataTableCell>
                    <DataTableCell>
                      {motivo.ativo
                        ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
                        : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                    </DataTableCell>
                    <DataTableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => handleOpenDialog(motivo)}
                          data-testid={`button-edit-motivo-desktop-${motivo.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {motivo.ativo && (
                          <Button
                            size="icon" variant="ghost"
                            onClick={() => handleDelete(motivo.id)}
                            data-testid={`button-delete-motivo-desktop-${motivo.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}
    </div>
  );
}
